/**
 * Owns the sheet-and-flow state `PoolAwardsCard` deliberately does not, so
 * `PoolSettingsPane.tsx` gains exactly one new line of JSX and one import.
 * Mounted into the reserved `awards` slot.
 */
import { useAwardCustomList } from "@sportsmet/contest-sdk/private";
import { type ReactNode, useState } from "react";

import { AwardTypeFormSheet } from "./AwardTypeFormSheet";
import { AwardTypeRemoveFlow } from "./AwardTypeRemoveFlow";
import { PoolAwardsCard } from "./PoolAwardsCard";
import type { typeAwardKind } from "./poolAwardsCopy";
import type { typeAwardTypeRow } from "./poolAwardsModel";

export type typePoolAwardsPanelProps = { poolId: string };

type typeFormTarget = {
  kind: typeAwardKind;
  editing: {
    awardTypeId: string;
    name: string;
    color: string;
    status: "active" | "archived";
    grantCount: number;
  } | null;
};

type typeRemoveTarget = { row: typeAwardTypeRow; kind: typeAwardKind };

export const PoolAwardsPanel = ({
  poolId,
}: typePoolAwardsPanelProps): ReactNode => {
  const list = useAwardCustomList(
    { poolId },
    { staleTime: 0, refetchOnMount: "always" },
  );

  const [formTarget, setFormTarget] = useState<typeFormTarget | null>(null);
  const [removeTarget, setRemoveTarget] = useState<typeRemoveTarget | null>(
    null,
  );

  function findRow(
    awardTypeId: string,
  ): { row: typeAwardTypeRow; kind: typeAwardKind } | null {
    const medals = (list.data?.data?.medals ??
      []) as readonly typeAwardTypeRow[];
    const trophies = (list.data?.data?.trophies ??
      []) as readonly typeAwardTypeRow[];
    const medalRow = medals.find((row) => row.awardTypeId === awardTypeId);
    if (medalRow) return { row: medalRow, kind: "medal" };
    const trophyRow = trophies.find((row) => row.awardTypeId === awardTypeId);
    if (trophyRow) return { row: trophyRow, kind: "trophy" };
    return null;
  }

  return (
    <>
      <PoolAwardsCard
        poolId={poolId}
        onCreate={(kind) => {
          setFormTarget({ kind, editing: null });
        }}
        onOpenEdit={(awardTypeId) => {
          const found = findRow(awardTypeId);
          if (found === null) return;
          setFormTarget({
            kind: found.kind,
            editing: {
              awardTypeId: found.row.awardTypeId,
              name: found.row.name,
              color: found.row.color,
              status: found.row.status,
              grantCount: found.row.grantCount,
            },
          });
        }}
        onRemove={(awardTypeId) => {
          const found = findRow(awardTypeId);
          if (found === null) return;
          setRemoveTarget(found);
        }}
      />
      <AwardTypeFormSheet
        open={formTarget !== null}
        poolId={poolId}
        kind={formTarget?.kind ?? "medal"}
        editing={formTarget?.editing ?? null}
        onOpenChange={(open) => {
          if (!open) setFormTarget(null);
        }}
        onSaved={() => {
          setFormTarget(null);
        }}
      />
      <AwardTypeRemoveFlow
        poolId={poolId}
        target={removeTarget}
        onDone={() => {
          setRemoveTarget(null);
        }}
      />
    </>
  );
};
